import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchGalleri, fetchDahliaBulbs } from '../lib/cms';
import GalleryLightbox from '../components/GalleryLightbox';

export default function GalleriPage() {
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const [entries, bulbs] = await Promise.all([
          fetchGalleri(),
          fetchDahliaBulbs().catch(() => []),
        ]);
        const extracted = [];

        // Byg opslagstabel direkte fra knoldene på bestillingssiden
        const bulbImageToNameMap = {};
        (bulbs || []).forEach((b) => {
          const bName = b.navn || b.name;
          const bImg = b.billede || b.image;
          if (bName && bImg) {
            const imgList = Array.isArray(bImg) ? bImg : [bImg];
            imgList.forEach((u) => {
              if (u) {
                const parts = u.split('/');
                const file = parts[parts.length - 1].split('.')[0].toLowerCase();
                bulbImageToNameMap[file] = bName;
              }
            });
          }
        });

        // Ensretning af navne, så de stemmer 100% overens med bestillingssiden
        const fileNameToNameMap = {
          'barberry-bluebird-366': 'Barberry Bluebird',
          'blackberry-ice-394': 'Blackberry Ice',
          'checkers-474': 'Checkers',
          'creme-de-cassis-3227': 'Creme De Cassis',
          'creme-de-cognac-514': 'Creme De Cognac',
          'danjo-doc-530': 'Danjo Doc',
          'ferncliff-illusion-574': 'Ferncliff Illusion',
          'fubuki-lyseroed-594': 'Fubuki',
          'galley-art-fair-606': 'Gallery Art Fair',
          'gallery-art-deco-602': 'Gallery Art Deco',
          'gh-lammerse-618': 'GH Lammerse',
          'hapet-mona-lisa-694': 'Hapet Mona Lisa',
          'kidd-climax-894': 'Kidd Climax',
          'medium-akita-001-au-heis-2-lr-3210': 'Atika', // Ensrettet med bestillingssiden (Atika)
          'oslo-1078': 'Oslo',
          'president-saint-grilles-1142': 'President Saint Grilles',
          'rebecca-lynn-1182': 'Rebecca Lynn',
          'seduction-2544': 'Seduction',
          'seniors-hope-3450': 'Seniors Hope',
          'seniors-hope-_3450': 'Seniors Hope',
          'tomo-2398': 'Tomo',
          'wine-eyed-jill-2328': 'Wine eyed Jill', // Ensrettet med bestillingssiden (Wine eyed Jill)
          'write-polveton-2323': 'Write Polveton', // Ensrettet med bestillingssiden (Write Polveton)
          'sebastian-3480': 'Sebastian',
          'a215-scaled': 'Marble Ball',
          'a216-scaled': 'Marble Ball',
          'a217-scaled': 'Hvid Pompon',
          'a218-scaled': 'Hvid Pompon',
          'a241-scaled': 'Hapet red Jack',
        };

        // Ensretningstabeller for eventuelle variationer
        const normalizeTitleToOrderPage = (title) => {
          if (!title) return '';
          const lower = title.trim().toLowerCase();
          if (lower === 'akita') return 'Atika';
          if (lower === 'white polveton') return 'Write Polveton';
          if (lower === 'wine eyed jill') return 'Wine eyed Jill';
          return title.trim();
        };

        const getNameFromUrl = (url) => {
          if (!url) return '';
          const parts = url.split('/');
          const filename = parts[parts.length - 1].split('.')[0].toLowerCase();
          
          if (bulbImageToNameMap[filename]) {
            return bulbImageToNameMap[filename];
          }

          if (fileNameToNameMap[filename]) {
            return fileNameToNameMap[filename];
          }

          for (const [key, val] of Object.entries(fileNameToNameMap)) {
            if (filename.includes(key)) {
              return val;
            }
          }

          for (const [key, val] of Object.entries(bulbImageToNameMap)) {
            if (filename.includes(key)) {
              return val;
            }
          }

          return '';
        };

        // 1. Indsaml alle dahlia-blomster fra bestillingssiden (knoldsortimentet)
        // så samtlige blomster med billede og navn optræder i galleriet
        const seenUrls = new Set();
        const seenPhotoKeys = new Set();

        const getPhotoDedupKey = (url) => {
          if (!url) return '';
          const parts = url.split('/');
          const filename = parts[parts.length - 1].split('.')[0].toLowerCase();
          const match = filename.match(/^(a\d+)[-_]/i);
          return match ? match[1] : filename;
        };

        (bulbs || []).forEach((b) => {
          const bName = b.navn || b.name;
          const bImg = b.billede || b.image;
          if (bName && bImg) {
            const imgList = Array.isArray(bImg) ? bImg : [bImg];
            imgList.forEach((imgUrl) => {
              if (imgUrl) {
                const photoKey = getPhotoDedupKey(imgUrl);
                if (seenUrls.has(imgUrl) || (photoKey && seenPhotoKeys.has(photoKey))) {
                  return;
                }
                seenUrls.add(imgUrl);
                if (photoKey) seenPhotoKeys.add(photoKey);

                const flowerName = normalizeTitleToOrderPage(bName);
                extracted.push({
                  url: imgUrl,
                  name: flowerName,
                });
              }
            });
          }
        });

        // 2. Tilføj elementer oprettet i galleri-samlingen
        (entries || []).forEach((entry) => {
          const rawTitle = entry.titel || entry.title;
          let entryTitle =
            (typeof rawTitle === 'object' ? rawTitle?.da : rawTitle) || '';
          entryTitle = normalizeTitleToOrderPage(entryTitle);

          // 2a. Tjek enkelt image (eller image-array)
          const entryImg = entry.billede || entry.image;
          if (entryImg) {
            const imgs = Array.isArray(entryImg) ? entryImg : [entryImg];
            imgs.forEach((imgUrl) => {
              if (imgUrl) {
                const photoKey = getPhotoDedupKey(imgUrl);
                if (seenUrls.has(imgUrl) || (photoKey && seenPhotoKeys.has(photoKey))) {
                  return;
                }
                seenUrls.add(imgUrl);
                if (photoKey) seenPhotoKeys.add(photoKey);

                const derivedName = entryTitle || getNameFromUrl(imgUrl);
                extracted.push({
                  url: imgUrl,
                  name: derivedName,
                });
              }
            });
          }

          // 2b. Tjek images-array (flere billeder)
          const entryImgs = entry.billeder || entry.images;
          if (entryImgs && Array.isArray(entryImgs)) {
            entryImgs.forEach((imgUrl, idx) => {
              if (imgUrl) {
                const photoKey = getPhotoDedupKey(imgUrl);
                if (seenUrls.has(imgUrl) || (photoKey && seenPhotoKeys.has(photoKey))) {
                  return;
                }
                seenUrls.add(imgUrl);
                if (photoKey) seenPhotoKeys.add(photoKey);

                let name =
                  entryTitle && entryTitle.toLowerCase() !== 'galleri'
                    ? entry.images.length > 1
                      ? `${entryTitle} (${idx + 1})`
                      : entryTitle
                    : '';

                if (!name) {
                  name = getNameFromUrl(imgUrl);
                }

                extracted.push({
                  url: imgUrl,
                  name: name,
                });
              }
            });
          }
        });

        // Sorter i alfabetisk rækkefølge efter navn (A-Å)
        const sorted = extracted.sort((a, b) => {
          const nameA = a.name.trim();
          const nameB = b.name.trim();

          // Hvis begge har navne, sorter alfabetisk
          if (nameA && nameB) {
            return nameA.localeCompare(nameB, 'da', { sensitivity: 'base' });
          }
          // Placér navngivne billeder øverst
          if (nameA && !nameB) return -1;
          if (!nameA && nameB) return 1;
          return 0;
        });

        setGalleryItems(sorted);
      } catch (err) {
        console.error('Fejl ved indlæsning af galleri:', err);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-[#9b4d85] mb-4" />
          <p className="text-forest-900 font-medium">Henter galleri...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20">
      <Helmet>
        <title>Galleri | Dahliahjørnet - H.J.K. Multiservice</title>
        <meta
          name="description"
          content="Se smukke billeder af vores dahlia-blomster, pluk-selv mark og hyggelige gård i Vandel."
        />
      </Helmet>

      {/* Header Banner */}
      <div className="relative h-[40vh] min-h-[300px] bg-forest-950 flex items-center justify-center text-center px-6">
        <img
          src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/c347dbd5-ff95-4119-bc58-7c327c410cd6/a215-scaled.jpg"
          alt="Dahliahjørnet Galleri"
          className="absolute inset-0 w-full h-full object-cover object-center opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-950 via-forest-950/50 to-transparent" />
        <div className="relative z-10 max-w-4xl">
          <span className="text-white uppercase tracking-[0.25em] font-bold text-xs sm:text-sm mb-3 block">
            Smukke øjeblikke
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-white leading-tight">
            Galleri
          </h1>
        </div>
      </div>

      {/* Gallery Grid Section */}
      <div className="max-w-7xl mx-auto px-6 mt-16">
        <div className="flex flex-col gap-2 mb-12 text-center max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-forest-950">
            Oplev vores farverige paradis
          </h2>
          <p className="text-charcoal-600 text-sm sm:text-base">
            Gå på opdagelse i billederne fra vores hyggelige gård i Vandel. Billederne er sorteret i alfabetisk rækkefølge. Klik på et billede for at åbne det i stor visning, og swipe nemt igennem på både mobil, tablet og desktop.
          </p>
        </div>

        {galleryItems.length === 0 ? (
          <div className="bg-white p-12 rounded-3xl text-center border border-cream-200">
            <i className="fa-solid fa-images text-4xl text-cream-400 mb-3 block" />
            <p className="text-charcoal-600 font-medium">Galleriet er i øjeblikket tomt.</p>
          </div>
        ) : (
          <GalleryLightbox images={galleryItems} />
        )}
      </div>
    </div>
  );
}
